import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Wallet, AlertTriangle, CheckCircle2, Send, Clock, ChevronDown } from "lucide-react";
import { API, authHeaders } from "@/constants/roles";
import { slotRows } from "@/constants/paymentMethods";

const REQUEST_KINDS = [
  { id: "all", label: "All payment details" },
  { id: "primary", label: "Primary method only" },
  { id: "backup", label: "Backup method only" },
  { id: "replacement", label: "Updated / replacement details" },
];

const STATUS_CHIP = {
  complete: { label: "Payment details complete", cls: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300" },
  pending: { label: "Payment details pending approval", cls: "border-amber-500/30 bg-amber-500/10 text-amber-300" },
  partial: { label: "Payment details incomplete", cls: "border-amber-500/30 bg-amber-500/10 text-amber-300" },
  missing: { label: "Payment details missing", cls: "border-rose-500/30 bg-rose-500/10 text-rose-300" },
};

const SlotCard = ({ testid, title, slot, complete, methods, missingText, optional = false }) => (
  <div data-testid={`${testid}-view`} className="rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-subtle)] p-4">
    <div className="flex items-center justify-between gap-2">
      <p className="text-[10px] font-bold uppercase tracking-wider text-[var(--pv-text-4)]">{title}</p>
      {optional && !slot ? (
        <span data-testid={`${testid}-status`} className="rounded-full border border-[var(--pv-border)] bg-[var(--pv-card)] px-2 py-0.5 text-[9px] font-bold uppercase text-[var(--pv-text-4)]">Optional — not set</span>
      ) : (
        <span data-testid={`${testid}-status`} className={`rounded-full border px-2 py-0.5 text-[9px] font-bold uppercase ${complete ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300" : "border-amber-500/30 bg-amber-500/10 text-amber-300"}`}>
          {complete ? "Complete" : "Incomplete"}
        </span>
      )}
    </div>
    {!slot ? (
      <p className="mt-2 text-xs font-medium text-[var(--pv-text-4)]">{missingText}</p>
    ) : (
      <>
        <p data-testid={`${testid}-method`} className="mt-2 font-display text-sm font-bold text-[var(--pv-text)]">{methods?.[slot.method]?.label || slot.method}</p>
        <dl className="mt-1.5 space-y-1">
          {slotRows(slot, methods).map((r) => (
            <div key={r.label} className="flex items-baseline justify-between gap-3 text-xs">
              <dt className="shrink-0 font-semibold text-[var(--pv-text-4)]">{r.label}</dt>
              <dd className={`break-all text-right font-medium ${r.value ? "text-[var(--pv-text-2)]" : "italic text-amber-400/80"}`}>{r.value || "missing"}</dd>
            </div>
          ))}
        </dl>
      </>
    )}
  </div>
);

// Owner view of a worker's payout setup + the "Request Payment Details" workflow.
// Used in My Team → worker profile and (compact) inside the payment modal.
export const WorkerPayoutSection = ({ workerId, compact = false, methods: methodsProp, onChanged }) => {
  const [data, setData] = useState(null);
  const [denied, setDenied] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [meta, setMeta] = useState(methodsProp || null);

  useEffect(() => {
    if (methodsProp) { setMeta(methodsProp); return; }
    axios.get(`${API}/payment-methods/meta`, { headers: authHeaders() })
      .then((r) => setMeta(r.data.methods))
      .catch(() => {});
  }, [methodsProp]);

  const load = useCallback(() => {
    if (!workerId) return;
    axios.get(`${API}/admin/workers/${workerId}/payment-details`, { headers: authHeaders() })
      .then((r) => { setData(r.data); setDenied(false); })
      .catch((e) => { if (e.response?.status === 403) setDenied(true); });
  }, [workerId]);
  useEffect(load, [load]);

  const sendRequest = async (kind) => {
    setBusy(true);
    setMenuOpen(false);
    try {
      await axios.post(`${API}/admin/workers/${workerId}/payment-requests`, { kind }, { headers: authHeaders() });
      toast.success("Request sent — the worker gets an in-site notification and one email.");
      load();
      onChanged?.();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.error === "already_pending") toast.info("That exact request is already pending for this worker.");
      else toast.error(typeof d === "string" ? d : "Could not send the request.");
    } finally { setBusy(false); }
  };

  // dismiss the kind menu on Escape / outside click (no destructive choice required to back out)
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e) => { if (e.key === "Escape") setMenuOpen(false); };
    const onClick = (e) => { if (!e.target.closest?.("[data-testid='request-kind-menu']") && !e.target.closest?.("[data-testid='request-payment-details-btn']")) setMenuOpen(false); };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => { document.removeEventListener("keydown", onKey); document.removeEventListener("mousedown", onClick); };
  }, [menuOpen]);

  if (denied) return null;
  if (!data) return <p className="py-4 text-center"><Loader2 className="mx-auto h-4 w-4 animate-spin text-cyan-400" /></p>;

  const chip = STATUS_CHIP[data.status] || STATUS_CHIP.missing;
  const pending = data.pending_request;
  const m = meta;

  return (
    <div data-testid="worker-payout-section" className={compact ? "" : "mt-1"}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span data-testid="payout-overall-status" className={`rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase ${chip.cls}`}>{chip.label}</span>
        {data.updated_at && (
          <span className="text-[10px] font-medium text-[var(--pv-text-4)]">Updated {new Date(data.updated_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>
        )}
      </div>

      {/* compact mode (payment modal): caller already renders the slot rows — only request controls here */}
      {!compact && (
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <SlotCard testid="payout-primary" title="Primary payment method" slot={data.primary} complete={data.primary_complete} methods={m} missingText="Not set — worker hasn't added one yet." />
          <SlotCard testid="payout-backup" title="Backup payment method (optional)" slot={data.backup} complete={data.backup_complete} methods={m} missingText="None — optional fallback in case the primary rail fails." optional />
        </div>
      )}

      {/* request workflow */}
      <div className="relative mt-3">
        {pending ? (
          <div data-testid="payout-request-pending" className="flex items-center gap-2 rounded-2xl border border-amber-500/25 bg-amber-500/5 px-4 py-2.5 text-xs font-semibold text-amber-300">
            <Clock className="h-3.5 w-3.5" />
            Request pending — {REQUEST_KINDS.find((k) => k.id === pending.kind)?.label} · sent {new Date(pending.requested_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
            {pending.emailed ? " · worker emailed" : ""}
          </div>
        ) : (
          <>
            <button
              data-testid="request-payment-details-btn"
              onClick={() => setMenuOpen((v) => !v)}
              disabled={busy}
              className="flex items-center gap-1.5 rounded-full bg-amber-500 px-4 py-2 text-xs font-bold text-amber-950 transition-colors hover:bg-amber-400 disabled:opacity-50"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
              Request payment details <ChevronDown className="h-3 w-3" />
            </button>
            {menuOpen && (
              <div data-testid="request-kind-menu" className="absolute z-20 mt-2 w-64 rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-card)] p-1.5 shadow-2xl">
                {REQUEST_KINDS.map((k) => (
                  <button key={k.id} data-testid={`request-kind-${k.id}`} onClick={() => sendRequest(k.id)} className="w-full rounded-xl px-3.5 py-2.5 text-left text-xs font-bold text-[var(--pv-text-2)] transition-colors hover:bg-[var(--pv-subtle)] hover:text-cyan-300">
                    {k.label}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* lightweight audit trail */}
      {!compact && (data.requests || []).length > 0 && (
        <div data-testid="payout-request-history" className="mt-4">
          <p className="text-[10px] font-bold uppercase tracking-wider text-[var(--pv-text-4)]">Request history</p>
          <ul className="mt-1.5 space-y-1">
            {data.requests.slice(0, 5).map((r) => (
              <li key={r.id} data-testid={`request-history-${r.id}`} className="flex flex-wrap items-center gap-2 text-[11px] font-medium text-[var(--pv-text-4)]">
                {r.status === "completed" ? <CheckCircle2 className="h-3 w-3 text-emerald-400" /> : <Clock className="h-3 w-3 text-amber-400" />}
                <span className="capitalize">{r.kind}</span> · requested {new Date(r.requested_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                {r.completed_at && <> · completed {new Date(r.completed_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</>}
                <span className={`rounded-full border px-1.5 py-0.5 text-[9px] font-bold uppercase ${r.status === "completed" ? "border-emerald-500/30 text-emerald-300" : "border-amber-500/30 text-amber-300"}`}>{r.status}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {!compact && (
        <p className="mt-3 flex items-center gap-1.5 text-[10px] text-[var(--pv-text-4)]">
          <Wallet className="h-3 w-3" /> Workers manage these details themselves — you request changes, they update them. Nothing here moves money.
        </p>
      )}
    </div>
  );
};
